import { useState, useEffect } from 'react'
import toast from 'react-hot-toast'
import { PhotoAnalysisResult, Product, CATEGORIES } from '../types'
import { formatPrice } from '../utils/formatters'

interface AiProductConfirmModalProps {
  analysisResult: PhotoAnalysisResult
  store: string
  onConfirm: (product: Omit<Product, 'prices'> & {
    store: string
    price: number
    shelfNumber?: string
    storeName?: string
  }) => void
  onCancel: () => void
  isCreating: boolean
}

const AiProductConfirmModal = ({ 
  analysisResult, 
  store, 
  onConfirm, 
  onCancel, 
  isCreating 
}: AiProductConfirmModalProps) => {
  const [productName, setProductName] = useState('')
  const [storeName, setStoreName] = useState('')
  const [selectedStore, setSelectedStore] = useState(store)
  const [price, setPrice] = useState('')
  const [category, setCategory] = useState('Muu')
  const [shelfNumber, setShelfNumber] = useState('')
  const [showRawText, setShowRawText] = useState(false)

  useEffect(() => {
    if (analysisResult.aiAnalysis) {
      setProductName(analysisResult.aiAnalysis.completedProductName || analysisResult.aiAnalysis.productName || '')
      setStoreName(analysisResult.aiAnalysis.storeName || '')
      setCategory(analysisResult.aiAnalysis.category || 'Muu')
    }
    
    if (analysisResult.detectedPrice) {
      setPrice(analysisResult.detectedPrice.toFixed(2))
    }
  }, [analysisResult])

  useEffect(() => {
    setSelectedStore(store)
  }, [store])

  const handleConfirm = () => {
    if (!productName.trim()) {
      toast.error('Tuotteen nimi on pakollinen')
      return
    }

    const parsedPrice = parseFloat(price)
    if (isNaN(parsedPrice) || parsedPrice <= 0) {
      toast.error('Anna kelvollinen hinta')
      return
    }

    const product = {
      name: productName.trim(),
      category: category as typeof CATEGORIES[number],
      isFavorite: false,
      store: selectedStore,
      price: parsedPrice,
      shelfNumber: shelfNumber.trim() || undefined,
      storeName: storeName.trim() || undefined
    }

    onConfirm(product)
  }

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return 'text-green-600 bg-green-50'
    if (confidence >= 0.6) return 'text-yellow-600 bg-yellow-50'
    return 'text-red-600 bg-red-50'
  }

  const getConfidenceText = (confidence: number) => {
    if (confidence >= 0.8) return 'Erittäin luotettava'
    if (confidence >= 0.6) return 'Melko luotettava'
    return 'Epävarma - tarkista tiedot'
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <h2 className="text-xl font-bold text-gray-900">
              Vahvista tuotetiedot
            </h2>
            <button
              onClick={onCancel}
              disabled={isCreating}
              className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* AI Analysis Confidence */}
          {analysisResult.aiAnalysis && (
            <div className={`mb-6 p-4 rounded-lg ${getConfidenceColor(analysisResult.aiAnalysis.confidence)}`}>
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-medium">AI-analyysi: </span>
                  <span>{getConfidenceText(analysisResult.aiAnalysis.confidence)}</span>
                </div>
                <span className="text-sm">
                  {Math.round(analysisResult.aiAnalysis.confidence * 100)}% varmuus
                </span>
              </div>
            </div>
          )}

          <form onSubmit={(e) => { e.preventDefault(); handleConfirm(); }} className="space-y-4">
            {/* Product Name */}
            <div>
              <label htmlFor="productName" className="block text-sm font-medium text-gray-700 mb-1">
                Tuotteen nimi *
              </label>
              <input
                id="productName"
                type="text"
                value={productName}
                onChange={(e) => setProductName(e.target.value)}
                disabled={isCreating}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                placeholder="Esim. Maito"
              />
            </div>

            {/* Store Specific Name */}
            <div>
              <label htmlFor="storeName" className="block text-sm font-medium text-gray-700 mb-1">
                Kauppakohtainen nimi
              </label>
              <input
                id="storeName"
                type="text"
                value={storeName}
                onChange={(e) => setStoreName(e.target.value)}
                disabled={isCreating}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                placeholder="Esim. Pirkka Kevytmaito"
              />
            </div>

            {/* Price */}
            <div>
              <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-1">
                Hinta (€) *
              </label>
              <input
                id="price"
                type="number"
                step="0.01"
                min="0"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                disabled={isCreating}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                placeholder="0.00"
              />
              {analysisResult.detectedPrice && (
                <p className="text-sm text-gray-600 mt-1">
                  AI havaitsi hinnan: {formatPrice(analysisResult.detectedPrice)}
                </p>
              )}
            </div>

            {/* Category */}
            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                Kategoria
              </label>
              <select
                id="category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                disabled={isCreating}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              >
                {CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            {/* Shelf Number */}
            <div>
              <label htmlFor="shelfNumber" className="block text-sm font-medium text-gray-700 mb-1">
                Hyllynumero
              </label>
              <input
                id="shelfNumber"
                type="text"
                value={shelfNumber}
                onChange={(e) => setShelfNumber(e.target.value)}
                disabled={isCreating}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                placeholder="Esim. A2"
              />
            </div>

            {/* Store Selector */}
            <div>
              <label htmlFor="store" className="block text-sm font-medium text-gray-700 mb-1">
                Kauppa *
              </label>
              <select
                id="store"
                value={selectedStore}
                onChange={(e) => setSelectedStore(e.target.value)}
                disabled={isCreating}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              >
                <option value="K-Market">K-Market</option>
                <option value="S-Market">S-Market</option>
                <option value="Lidl">Lidl</option>
                <option value="Prisma">Prisma</option>
                <option value="Citymarket">Citymarket</option>
                <option value="Alepa">Alepa</option>
                <option value="Sale">Sale</option>
                <option value="Muu">Muu</option>
              </select>
            </div>

            {/* Raw OCR Text Toggle */}
            <div>
              <button
                type="button"
                onClick={() => setShowRawText(!showRawText)}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                {showRawText ? 'Piilota' : 'Näytä'} tunnistettu teksti
              </button>
              
              {showRawText && (
                <div className="mt-2 p-3 bg-gray-100 rounded-md">
                  <h4 className="font-medium text-sm text-gray-700 mb-2">Tunnistettu teksti:</h4>
                  <div className="text-sm text-gray-600 space-y-1">
                    {analysisResult.extractedText.map((text, index) => (
                      <div key={index} className="font-mono text-xs">"{text}"</div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onCancel}
                disabled={isCreating}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Peruuta
              </button>
              <button
                type="submit"
                disabled={isCreating || !productName.trim() || !price}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isCreating ? (
                  <>
                    <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Luodaan...
                  </>
                ) : (
                  'Luo tuote'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default AiProductConfirmModal